import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export type MfaGateState = {
  loaded: boolean;
  required: boolean;          // org requires 2FA for this user's role
  enrolled: boolean;          // has a verified TOTP factor
  aal2: boolean;              // current session is AAL2
  needsEnroll: boolean;       // required && !enrolled
  needsVerify: boolean;       // required && enrolled && !aal2
};

const DEFAULT: MfaGateState = {
  loaded: false, required: false, enrolled: false, aal2: false,
  needsEnroll: false, needsVerify: false,
};

export function useMfaGate(): MfaGateState {
  const { user, currentOrg, currentRole, ready } = useAuth();
  const [state, setState] = useState<MfaGateState>(DEFAULT);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "MFA_CHALLENGE_VERIFIED" || event === "TOKEN_REFRESHED" || event === "SIGNED_IN") {
        setTick((t) => t + 1);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!ready || !user || !currentOrg || !currentRole) {
      setState(DEFAULT);
      return;
    }
    (async () => {
      const [{ data: flags }, { data: fac }, { data: a }] = await Promise.all([
        supabase
          .from("org_feature_flags")
          .select("mfa_required_roles")
          .eq("organization_id", currentOrg.id)
          .maybeSingle(),
        supabase.auth.mfa.listFactors(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      ]);
      if (cancelled) return;
      const roles = (flags?.mfa_required_roles ?? []) as string[];
      const required = roles.includes(currentRole);
      const enrolled = !!(fac?.totp?.some((f) => f.status === "verified"));
      const aal2 = a?.currentLevel === "aal2";
      setState({
        loaded: true,
        required,
        enrolled,
        aal2,
        needsEnroll: required && !enrolled,
        needsVerify: required && enrolled && !aal2,
      });
    })();
    return () => { cancelled = true; };
  }, [ready, user?.id, currentOrg?.id, currentRole, tick]);

  return state;
}
